import type NumberUnit from '@exodus/currency';
import { useSessionStorage } from '@passkeys/hooks';
import { useMemo, useState } from 'react';

import { type Asset } from '@/types';
import useBuyProvider from '@/ui/hooks/use-buy-provider';
import useConversion from '@/ui/hooks/use-conversion';
import useEvent from '@/ui/hooks/use-event';
import useFiatAsset from '@/ui/hooks/use-fiat-asset';
import useSupportedBuyCryptoAssets from '@/ui/hooks/use-supported-buy-crypto-assets';

const DEFAULT_FIAT_AMOUNT = '30';

type UseBuyPairOptions = {
  initialToAssetName?: string;
};

function useBuyPair({ initialToAssetName }: UseBuyPairOptions) {
  const providerName = useBuyProvider();
  const cryptoAssets = useSupportedBuyCryptoAssets(providerName) as Asset[];
  const fiatAsset = useFiatAsset();
  const cryptoAsset = ((initialToAssetName &&
    cryptoAssets.find((asset) => asset.name === initialToAssetName)) ||
    cryptoAssets[0]) as Asset;

  const convert = useConversion(cryptoAsset.name);

  const [fromAmount, setFromAmount] = useSessionStorage(
    'buy:from-amount',
    DEFAULT_FIAT_AMOUNT,
  );
  const fromPair = useMemo(
    () => ({
      amount: fiatAsset.currency.defaultUnit(fromAmount),
      asset: fiatAsset,
    }),
    [fiatAsset, fromAmount],
  );

  const [toPair, setToPair] = useState({
    amount: convert(fromPair.amount),
    asset: cryptoAsset,
  });

  const setAmount = (setPair) => (amount: NumberUnit) => {
    if (!amount) {
      return;
    }

    setPair((previousValue) => ({ ...previousValue, amount }));
  };

  const setToAsset = (assetName: string) => {
    const asset = cryptoAssets.find((asset) => asset.name === assetName);

    if (!asset) {
      return;
    }

    setToPair((previousValue) => ({ ...previousValue, asset }));
  };

  return {
    providerName,
    fromAsset: fromPair.asset,
    fromAmount: fromPair.amount,
    setFromAmount: useEvent((value) =>
      setFromAmount(value.toString({ unit: false })),
    ),
    toAsset: toPair.asset,
    setToAsset: useEvent(setToAsset),
    toAmount: toPair.amount,
    setToAmount: useEvent(setAmount(setToPair)),
    toAssets: cryptoAssets,
  };
}

export default useBuyPair;
